// Pass-rate leaderboard with the picked entity highlighted: top 3 +
// picked row + bottom 3 when the cohort doesn't fit; clicking a
// peer's bar switches the picker focus to them.

import React, { useContext, useMemo } from 'react'
import { isEmpty, isNil } from 'lodash'

import { Card } from '@/components/cards/Card'

import { CHART } from '@/components/charts/chartPalette'
import { PickEntityContext } from '../../helpers/dashboardContexts'
import { rankOf, LABEL_BY_SCOPE, useCohortContext } from '../../helpers/cohort'
import { escapeHtml } from '../../helpers/format'
import { ChartWidgetCard, axisTooltip, hBarBase } from '../chartWidget'

const TOP_N = 3
const BOTTOM_N = 3

// When the cohort doesn't fit in TOP_N + BOTTOM_N + 1, return top +
// selected (if not already in either slice) + bottom plus a gap marker.
const buildDisplayRows = (sortedCohort, selectedId) => {
  const total = sortedCohort.length
  if (total <= TOP_N + BOTTOM_N + 1) {
    return { rows: sortedCohort, gapAt: -1, hiddenCount: 0 }
  }
  const topSlice = sortedCohort.slice(0, TOP_N)
  const bottomSlice = sortedCohort.slice(total - BOTTOM_N)
  const selectedItem = sortedCohort.find((item) => item.id === selectedId)
  const alreadyShown =
    selectedItem && (topSlice.includes(selectedItem) || bottomSlice.includes(selectedItem))
  const middle = selectedItem && !alreadyShown ? [selectedItem] : []
  const rows = [...topSlice, ...middle, ...bottomSlice]
  const hiddenCount = total - rows.length
  // Gap sits visually between the picked entity and the bottom 3.
  const gapAt = TOP_N + middle.length
  return { rows, gapAt, hiddenCount }
}

export const PeerLeaderboardWidget = ({ scope }) => {
  const cohort = useCohortContext(scope)
  const pickEntity = useContext(PickEntityContext)
  const entityLabel = LABEL_BY_SCOPE[scope]

  const sortedCohort = useMemo(
    () => [...(cohort.normalized || [])].sort((rowA, rowB) => rowB.passRate - rowA.passRate),
    [cohort.normalized]
  )
  const { rows, gapAt, hiddenCount } = useMemo(
    () => buildDisplayRows(sortedCohort, cohort.selectedId),
    [sortedCohort, cohort.selectedId]
  )
  const ranking = useMemo(
    () => rankOf(sortedCohort, cohort.selectedId, (row) => row.passRate),
    [sortedCohort, cohort.selectedId]
  )
  const selectedPassRate = useMemo(() => {
    const row = sortedCohort.find((candidate) => candidate.id === cohort.selectedId)
    return row ? row.passRate : null
  }, [sortedCohort, cohort.selectedId])
  const maxValue = cohort.cohortMax || 100

  // The gap marker becomes a bar-less axis category so the
  // "top 3 ... bottom 3" ellipsis survives the chart conversion.
  const chartRows = useMemo(() => {
    if (gapAt < 0 || hiddenCount === 0) {
      return rows
    }
    const gapRow = {
      id: '__gap__',
      name: `···  ${hiddenCount} ${entityLabel}${hiddenCount === 1 ? '' : 's'} hidden  ···`,
      passRate: null,
      isGap: true,
    }
    return [...rows.slice(0, gapAt), gapRow, ...rows.slice(gapAt)]
  }, [rows, gapAt, hiddenCount, entityLabel])

  const option = useMemo(() => {
    if (isEmpty(chartRows)) {
      return {}
    }
    return {
      tooltip: axisTooltip((params) => {
        const row = params?.[0]?.data?.row
        if (!row || row.isGap) {
          return ''
        }
        const suffix =
          row.id === params[0].data.selectedId ? '' : `<br/><em>Click to switch focus</em>`
        return `${escapeHtml(row.name)}<br/>${row.passRate.toFixed(1)}% ${cohort.metricLabel}${suffix}`
      }),
      ...hBarBase(
        chartRows.map((row) => row.name),
        {
          left: 150,
          right: 48,
          top: 8,
          xAxis: {
            max: Math.ceil(maxValue),
            axisLabel: { fontSize: 10, formatter: '{value}%' },
          },
        }
      ),
      series: [
        {
          type: 'bar',
          cursor: 'pointer',
          barWidth: '60%',
          data: chartRows.map((row) => ({
            value: row.isGap || isNil(row.passRate) ? null : parseFloat(row.passRate.toFixed(1)),
            row,
            selectedId: cohort.selectedId,
            // Click payload -- handlers read p.data, never closures.
            pick: row.isGap ? null : { value: row.id, label: row.name },
            itemStyle: {
              color: row.id === cohort.selectedId ? CHART.BRAND : CHART.MUTED,
              opacity: row.id === cohort.selectedId ? 1 : 0.45,
              borderRadius: [0, 3, 3, 0],
            },
          })),
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            formatter: ({ value }) => (isNil(value) ? '' : `${value}%`),
          },
        },
      ],
    }
  }, [chartRows, cohort.selectedId, maxValue])

  // Custom handler: the already-selected row no-ops so the click
  // doesn't re-fire the picker state on its current value.
  const events = useMemo(
    () => ({
      click: (params) => {
        const pick = params?.data?.pick
        if (!pick || pick.value === params?.data?.selectedId || !pickEntity) {
          return
        }
        pickEntity(pick)
      },
    }),
    [pickEntity]
  )

  if (cohort.loading) {
    return (
      <Card>
        <div className="entity-drawer-empty">Loading…</div>
      </Card>
    )
  }
  if (isEmpty(rows)) {
    return (
      <Card>
        <div className="entity-drawer-empty">No {entityLabel}s with data in this window.</div>
      </Card>
    )
  }

  return (
    <ChartWidgetCard
      title={`Pass rate vs other ${entityLabel}s`}
      info={`How the selected ${entityLabel}'s pass rate ranks against its peers in the current window.`}
      hint="Click any bar to switch focus"
      hintKind="picker"
      option={option}
      events={events}
      height={`${chartRows.length * 34 + 52}px`}
    >
      <div className="peer-leaderboard">
        <div className="peer-leaderboard-summary">
          {ranking && !isNil(selectedPassRate) ? (
            <>
              <span className="peer-leaderboard-rank">
                Rank <strong>{ranking.rank}</strong> of {ranking.total}
              </span>
              <span className="peer-leaderboard-summary-sep">·</span>
              <span>
                {selectedPassRate.toFixed(1)}% {cohort.metricLabel}
              </span>
              {!isNil(cohort.cohortAvg) && (
                <>
                  <span className="peer-leaderboard-summary-sep">·</span>
                  <span>
                    {entityLabel} avg {cohort.cohortAvg.toFixed(1)}%
                  </span>
                </>
              )}
            </>
          ) : (
            !isNil(cohort.cohortAvg) && (
              <span>
                {entityLabel} avg {cohort.cohortAvg.toFixed(1)}%
              </span>
            )
          )}
        </div>
      </div>
    </ChartWidgetCard>
  )
}
